import { useState, useEffect } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { styles } from './Login.styles'

type View = 'signin' | 'signup' | 'forgot' | 'reset'

// HTML type="email" accepts dot-less hostnames like "ali@elevensignal".
// Require a real domain with a TLD (at least one dot + 2-char suffix).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const isValidEmail = (value: string) => EMAIL_RE.test(value.trim())

export default function Login() {
  const { signIn, signUp, isRecovery: authIsRecovery } = useAuth()
  const [view, setView] = useState<View>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [loading, setLoading] = useState(false)

  // Switch to reset view whenever useAuth detects a recovery session.
  // useAuth reads the URL hash synchronously at module load so this fires
  // even after Supabase clears the hash and before PASSWORD_RECOVERY replays.
  useEffect(() => {
    if (authIsRecovery) {
      setView('reset')
      setError('')
      setInfo('')
    }
  }, [authIsRecovery])

  const handleSignIn = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setError('')
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.')
      return
    }
    setLoading(true)
    try {
      await signIn(email, password)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSignUp = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setError('')
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.')
      return
    }
    setLoading(true)
    try {
      await signUp(email, password)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const handleForgot = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setError('')
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.')
      return
    }
    setLoading(true)
    try {
      const { data: exists, error: rpcError } = await supabase.rpc('check_email_exists', { input_email: email })
      if (rpcError) throw rpcError
      if (!exists) {
        setError('No account found with this email address.')
        return
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      })
      if (error) throw error
      setInfo(`Reset link sent to ${email}. Check your inbox.`)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      setInfo('Password updated. You can now sign in.')
      setView('signin')
      setPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setLoading(false)
    }
  }

  const switchView = (v: View) => {
    setView(v)
    setError('')
    setInfo('')
  }

  return (
    <div className="auth-page" style={styles.page}>
      <div className="auth-card" style={styles.container}>
        <div className="auth-header" style={styles.header}>
          <div className="auth-logo" style={styles.logoRow}>
            <img
              src="/logo-dark.png"
              alt="Brands Meet Creators"
              style={{ height: '28px', width: 'auto' }}
            />
          </div>
          <p className="auth-title" style={styles.subtitle}>
            {{
              signin: 'Sign in to your account',
              signup: 'Create your account',
              forgot: 'Reset your password',
              reset: 'Set a new password',
            }[view]}
          </p>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {info  && <div style={styles.infoBox}>{info}</div>}

        {/* ── Sign in ── */}
        {view === 'signin' && (
          <form onSubmit={handleSignIn} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" required style={styles.input} />
            </div>
            <div style={styles.field}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={styles.label}>Password</label>
              </div>
              <div style={styles.inputWrap}>
                <input type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required minLength={6} style={{ ...styles.input, paddingRight: '2.5rem' }} />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={styles.eyeBtn}>
                  {showPassword ? <EyeOff size={15} color="var(--text-tertiary)" /> : <Eye size={15} color="var(--text-tertiary)" />}
                </button>
              </div>
            </div>
            <button type="button" onClick={() => switchView('forgot')} style={styles.forgotLink}>
                  Forgot password?
                </button>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Loading...' : 'Continue'}
            </button>
          </form>
        )}

        {/* ── Sign up ── */}
        {view === 'signup' && (
          <form onSubmit={handleSignUp} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" required style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <div style={styles.inputWrap}>
                <input type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required minLength={6} style={{ ...styles.input, paddingRight: '2.5rem' }} />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={styles.eyeBtn}>
                  {showPassword ? <EyeOff size={15} color="var(--text-tertiary)" /> : <Eye size={15} color="var(--text-tertiary)" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Loading...' : 'Create account'}
            </button>
          </form>
        )}

        {/* ── Forgot password ── */}
        {view === 'forgot' && (
          <form onSubmit={handleForgot} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" required style={styles.input} />
            </div>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
            <button type="button" onClick={() => switchView('signin')} style={styles.backLink}>
              Back to sign in
            </button>
          </form>
        )}

        {/* ── Reset password (after clicking email link) ── */}
        {view === 'reset' && (
          <form onSubmit={handleReset} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>New password</label>
              <div style={styles.inputWrap}>
                <input type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required minLength={6} style={{ ...styles.input, paddingRight: '2.5rem' }} />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={styles.eyeBtn}>
                  {showPassword ? <EyeOff size={15} color="var(--text-tertiary)" /> : <Eye size={15} color="var(--text-tertiary)" />}
                </button>
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Confirm new password</label>
              <div style={styles.inputWrap}>
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••" required minLength={6} style={{ ...styles.input, paddingRight: '2.5rem' }} />
                <button type="button" onClick={() => setShowConfirmPassword(v => !v)} style={styles.eyeBtn}>
                  {showConfirmPassword ? <EyeOff size={15} color="var(--text-tertiary)" /> : <Eye size={15} color="var(--text-tertiary)" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>
        )}

        {(view === 'signin' || view === 'signup') && (
          <>
            <div style={styles.divider}>
              <span style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <span style={styles.dividerLine} />
            </div>
            <button
              type="button"
              onClick={() => switchView(view === 'signin' ? 'signup' : 'signin')}
              style={styles.altButton}
            >
              {view === 'signin' ? 'Create a new account' : 'Sign in to existing account'}
            </button>
          </>
        )}

      </div>
    </div>
  )
}
